/**
 * Shared monitoring identity — sets Sentry user + Clarity identify in one place.
 */

import { useAuthStore } from '@/stores/auth.store';
import type { UserSession } from '@/types/auth';

import { clearClarityIdentity, identifyClarity } from './clarity';
import { Sentry } from './sentry';

const SSI_ROLES = new Set(['Admin', 'SSIAdministrator', 'SSIEmployee']);

/**
 * Map a UserSession (+ impersonation flag) onto Sentry and Clarity.
 */
export function setMonitoringUser(session: UserSession): void {
  const impersonating = Boolean(useAuthStore.getState().realUser);
  const isSSI = SSI_ROLES.has(session.role);

  Sentry.setUser({
    id: String(session.userId),
    email: session.email,
  });
  Sentry.setTags({
    role: session.role,
    companyId: String(session.companyId),
    authProvider: session.authProvider ?? 'unknown',
    impersonating: impersonating ? 'true' : 'false',
    isSSI: isSSI ? 'true' : 'false',
  });

  identifyClarity({
    userId: session.userId,
    role: session.role,
    companyId: session.companyId,
    impersonating,
    isSSI,
  });
}

/** Clear Sentry + Clarity identity on logout. */
export function clearMonitoringUser(): void {
  Sentry.setUser(null);
  clearClarityIdentity();
}
